import { useQueryClient } from "@tanstack/react-query";
import SOURCES from "../../../../utils/constants";
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useDebounce from "../../../../utils/customHooks";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import { useDestinations } from "../../../../queries/common/use-destinations";
import CommonCard from "../../../global/card";
import CommonSkeletonLoader from "../../../global/loading";
import CommonPagination from "../../../global/pagination";
import EnhancedLeadsSentList from "./EnhancedLeadsSentList";
import EnhancedAdvanceSearchLeadsSent from "./EnhancedAdvanceSearchLeadsSent";

const EnhancedLeadsSent = () => {
    const queryClient = useQueryClient();
    const [searchLeadsSent, setSearchLeadsSent] = useState("");

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(
        searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
        10
    );
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);
    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
    const [advanceSearch, setAdvanceSearch] = useState({
        advance_search_enabled:
            SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER,
        startDate: "",
        endDate: "",
        destination: "",
        searchTerm: "",
    });

    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });
    const [leadsSentViewType, setLeadsSentViewType] = useState(() => {
        return localStorage.getItem("leadsSentViewType") || "GRID";
    });
    const debouncedSearchTerm = useDebounce(searchLeadsSent, 500);

    // Key params for main listing
    const keyParams = useMemo(() => ({
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    }), [pagination.offset, pagination.limit, sort.sortType, sort.sortOrder]);

    // Key params for search (including advanced search)
    const searchKeyParams = useMemo(() => ({
        // We use listing pagination for search results too, or could separate if needed
        // The original logic seemed to share or reset pagination. Let's use current pagination.
        offset: 0, // Reset offset for search typically, or manage separate paginationSearch state
        limit: pagination.limit,
        q: debouncedSearchTerm,
        advance_search: advanceSearch.advance_search_enabled,
        startdate: advanceSearch.startDate,
        enddate: advanceSearch.endDate,
        destination: advanceSearch.destination,
        term: advanceSearch.searchTerm
    }), [debouncedSearchTerm, advanceSearch, pagination.limit]);

    const { data: leadsData, isLoading: leadsLoading, isFetching: leadsFetching } = useGenericQuery({
        endpoint: "leadstatistic",
        queryKey: QUERY_KEYS.leadsSent.lists({ ...keyParams }),
        queryParams: { ...keyParams },
        onError: () => { },
        onSuccess: () => { },
        // enabled: !debouncedSearchTerm && advanceSearch.advance_search_enabled === SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER
    })

    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "leadstatistic/search",
        queryKey: QUERY_KEYS.leadsSent.lists({ ...searchKeyParams }),
        queryParams: { ...searchKeyParams },
        enabled: !!debouncedSearchTerm || advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER
    })

    const { data: destinationsData } = useDestinations()

    const isLoading = leadsLoading || searchDataLoading;

    // Determine final data
    const finalData = useMemo(() => {
        if (debouncedSearchTerm || advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER) {
            return searchData?.infos || []; // Use 'infos' as per old component
        }
        return leadsData?.infos || [];
    }, [debouncedSearchTerm, advanceSearch.advance_search_enabled, searchData, leadsData]);

    const totalRecords = useMemo(() => {
        if (debouncedSearchTerm || advanceSearch.advance_search_enabled !== SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER) {
            // Search API might not return count in same structure, verify if needed. 
            // Assuming similar structure or simple array length for now if count missing
            return searchData?.count || searchData?.infos?.length || 0;
        }
        return leadsData?.count || 0;
    }, [debouncedSearchTerm, advanceSearch.advance_search_enabled, searchData, leadsData]);


    const dataExist = finalData.length > 0;

    const handleViewTypeChange = (type) => {
        setLeadsSentViewType(type);
        localStorage.setItem("leadsSentViewType", type);
    };

    return (
        <CommonCard
            isLoading={{
                show: isLoading,
                loaderCmp: <CommonSkeletonLoader
                    type={leadsSentViewType === "GRID" ? "card" : "table"}
                />
            }}
            isSearch={{
                show: true,
                searchInput: searchLeadsSent,
                setSearchInput: setSearchLeadsSent,
                placeholder: "Search Leads Sent..."
            }}
            isAdvanceSearch={{
                show: true,
                children: <EnhancedAdvanceSearchLeadsSent
                    isOpen={showAdvanceSearch}
                    advanceSearch={advanceSearch}
                    setShowAdvanceSearch={setShowAdvanceSearch}
                    setAdvanceSearch={setAdvanceSearch}
                    getAdvanceSearchData={() => { }} // react-query handles refetch
                />
            }}
            isListGridView={{
                show: true,
                viewType: leadsSentViewType,
                setViewType: handleViewTypeChange,
                viewFor: "leadsSentViewType"

            }}
        >
            <EnhancedLeadsSentList
                isLoading={isLoading}
                leadsSent={finalData}
                sort={sort}
                setSort={setSort}
                viewType={leadsSentViewType}
            />

            {(dataExist && !debouncedSearchTerm &&
                advanceSearch.advance_search_enabled === SOURCES.ADVANCE_SEARCH_FIELDS.SEARCH_TYPE_NOT_FILTER) &&
                <CommonPagination
                    currentPage={pagination.page}
                    defaultLimit={pagination.limit}
                    totalRecords={totalRecords}
                    setCurrentPage={setPagination}
                    activePageRecords={finalData.length}
                    paginationFor="Leads Sent"
                    className="mt-4"
                />
            }
        </CommonCard>
    )
}

export default EnhancedLeadsSent;